import React, { useState } from "react";
import Navigation from "./Navigation";
import Card from "./Card";
import Input from "./Input";
import { Button } from "./Button";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import { useApi } from "../hooks/useApi";
import useInstanceInfo from "../hooks/useInstanceInfo";

const ContactUs = () => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const apiCall = useApi();
  const { info, loading: instanceLoading } = useInstanceInfo();
  const supportEmail = info.supportEmail;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiCall("POST", "/support/contact", { subject, description });
      setSubmitted(true);
      setSubject("");
      setDescription("");
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Mail className="h-8 w-8 text-accent-cyan" />
          <h1 className="text-2xl font-bold text-content-primary">Contact Us</h1>
        </div>
        {instanceLoading ? (
          <p className="text-content-secondary ml-11">Loading…</p>
        ) : !supportEmail ? (
          <p className="text-content-secondary ml-11">
            Support contact isn&apos;t configured for this installation. Ask your administrator.
          </p>
        ) : (
          <>
          <p className="text-content-secondary mb-8 ml-11">
            Have a question or need help? Send us a message and we'll get back to you.
          </p>

          {submitted && (
            <div className="mb-6 flex items-center gap-2 px-4 py-3 rounded-lg bg-status-success/10 border border-status-success/30 text-status-success">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">Thank you! We'll get back to you shortly.</span>
            </div>
          )}

          {error && (
            <div className="mb-6 flex items-center gap-2 px-4 py-3 rounded-lg bg-risk-critical/10 border border-risk-critical/30 text-risk-critical">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <Card variant="elevated">
            <form onSubmit={handleSubmit}>
              <Input
                label="Subject"
                name="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What is your question about?"
                required
              />
              <div className="mb-4">
                <label
                  className="block text-sm font-medium text-content-secondary mb-1"
                  htmlFor="description"
                >
                  Description
                  <span className="text-risk-critical ml-1">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your question or issue in detail..."
                  required
                  rows={6}
                  className="w-full px-3 py-2 rounded-lg bg-theme-tertiary border border-border text-content-primary placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan transition-colors"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={!subject.trim() || !description.trim() || submitting}
                >
                  {submitting ? "Sending..." : "Submit"}
                </Button>
              </div>
            </form>
          </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactUs;
